import type { DependencyType, EdgeRelation, LayoutEdge } from '../types';

/**
 * Edge styling tiers. Critical dependencies are solid; non-critical ones are
 * dashed and coloured by severity. `optional` covers inferred API consumption,
 * and `hasPart` is structural containment rather than a dependency.
 */
export type EdgeTier = 'critical' | 'high' | 'medium' | 'low' | 'optional' | 'unknown' | 'hasPart' | 'pendingTarget';

export const EDGE_COLORS: Record<EdgeTier, string> = {
  critical: '#f4534d',
  high: '#ff7043',
  medium: '#ffa726',
  low: '#ffc107',
  optional: '#6e7479',
  unknown: '#8b949e',
  hasPart: '#5c6470',
  pendingTarget: '#d500f9',
};

export const EDGE_WIDTHS: Record<EdgeTier, number> = {
  critical: 2.5,
  high: 2,
  medium: 1.75,
  low: 1.5,
  optional: 1.5,
  unknown: 1.5,
  hasPart: 1.25,
  pendingTarget: 2.5,
};

export const EDGE_LABELS: Record<EdgeTier, string> = {
  critical: 'Critical',
  high: 'Non-critical (high)',
  medium: 'Non-critical (medium)',
  low: 'Non-critical (low)',
  optional: 'API usage',
  unknown: 'Unknown',
  hasPart: 'Contains (hasPart)',
  pendingTarget: 'Target is pending (would fail sync)',
};

/** Tiers drawn as an unbroken line; everything else is dashed. */
export const SOLID_TIERS: ReadonlySet<EdgeTier> = new Set<EdgeTier>(['critical', 'hasPart']);

export function edgeTier(
  dependencyType: DependencyType | string | undefined,
  severity?: string,
  relation?: EdgeRelation,
): EdgeTier {
  if (relation === 'hasPart') return 'hasPart';
  if (dependencyType === 'optional') return 'optional';
  if (dependencyType === 'unknown') return 'unknown';
  if (dependencyType === 'noncritical') {
    const s = (severity ?? '').toLowerCase();
    if (s === 'high') return 'high';
    if (s === 'medium') return 'medium';
    return 'low';
  }
  return 'critical';
}

interface TopologyEdgeProps {
  edge: LayoutEdge;
}

export const TopologyEdgeLine = ({ edge }: TopologyEdgeProps) => {
  // A pending target would be rejected by the registry on sync, so it overrides
  // the usual criticality styling — this is a data problem, not a severity tier.
  const tier = edge.targetPending ? 'pendingTarget' : edgeTier(edge.dependencyType, edge.severity, edge.relation);
  const stroke = EDGE_COLORS[tier];

  return (
    <g>
      {edge.sections.map((section, i) => {
        const points: string[] = [];
        points.push(`${section.startPoint.x},${section.startPoint.y}`);
        for (const bp of section.bendPoints ?? []) {
          points.push(`${bp.x},${bp.y}`);
        }
        points.push(`${section.endPoint.x},${section.endPoint.y}`);

        return (
          <polyline
            key={`${edge.id}-section-${i}`}
            points={points.join(' ')}
            fill="none"
            stroke={stroke}
            strokeWidth={EDGE_WIDTHS[tier]}
            markerEnd={`url(#arrowhead-${tier})`}
            strokeDasharray={SOLID_TIERS.has(tier) ? undefined : '6,3'}
          />
        );
      })}
    </g>
  );
};
